<script setup lang="ts">
    import AppModalTitle from '@/components/modal/AppModalTitle.vue';
    import AppModalContent from '@/components/modal/AppModalContent.vue';
    import AppModalActions from '@/components/modal/AppModalActions.vue';
    import UploadMessageIcon from '@/views/case-settings/upload/ct/components/UploadMessageIcon.vue';
    import { computed } from 'vue';
    import { useHipSpineUploadViewModel as useHipSpineUpload } from '@/views/case-settings/upload/hip-spine/useHipSpineUploadViewModel';

    const upload = useHipSpineUpload();

    const fileCount = computed(() => {
        return upload.xrayFiles?.length ?? 0;
    });

    const messages = computed(() => {
        // Get patient identity check messages if available
        const patientCheck = upload.state.patientIdentityCheck;
        return patientCheck?.sampleFile?.messages ?? [];
    });
</script>

<template>
    <app-modal
        :persistent="true"
        width="800"
        :model-value="upload.isUploading || upload.isModalError || upload.isWaitingForConfirmation"
    >
        <v-card>
            <app-modal-title @close="upload.cancel">Hip Spine Upload</app-modal-title>

            <app-modal-content>
                <v-alert
                    v-if="upload.progress.error"
                    :text="upload.progress.error"
                    type="error"
                    variant="tonal"
                    class="mb-4"
                />

                <app-text v-if="fileCount > 0">
                    Uploading {{ fileCount }} Hip Spine {{ fileCount === 1 ? 'file' : 'files' }}
                </app-text>

                <div v-if="upload.isUploading">
                    <v-progress-linear
                        class="progress-bar"
                        stream
                        color="primary"
                        height="10"
                        :max="upload.progress.total ?? 0"
                        :buffer-value="upload.progress.current"
                    />
                </div>

                <v-data-table-virtual
                    v-if="messages.length > 0"
                    :headers="[
                        { title: 'Level', key: 'level' },
                        { title: 'Message', key: 'message' },
                    ]"
                    :items="messages"
                    height="400"
                    item-value="name"
                >
                    <!-- eslint-disable vue/valid-v-slot -->
                    <template #item.level="{ item }">
                        <UploadMessageIcon :level="item.level" />
                    </template>
                    <!-- eslint-enable vue/valid-v-slot -->
                </v-data-table-virtual>
                <div v-else class="mt-4 mb-4">
                    <app-text class="text-grey">
                        Your Hip Spine files are being validated and uploaded.
                    </app-text>
                </div>
            </app-modal-content>

            <app-modal-actions>
                <app-button-secondary width="100" class="mr-5" @click="upload.cancel">
                    Cancel
                </app-button-secondary>
                <app-button-primary
                    v-if="upload.isWaitingForConfirmation"
                    :loading="upload.isUploading"
                    @click="upload.userConfirmationUpload"
                >
                    Upload
                </app-button-primary>
                <app-button-primary
                    v-else
                    :loading="upload.isUploading"
                    :disabled="upload.isModalError"
                >
                    Upload
                </app-button-primary>
            </app-modal-actions>
        </v-card>
    </app-modal>
</template>

<!--suppress CssUnusedSymbol -->
<style scoped lang="scss">
    :deep(.v-progress-linear__background, .v-progress-linear__stream) {
        background-color: rgb(var(--v-theme-primary));
        opacity: 0.8;
    }

    :deep(.v-table .v-table__wrapper table tbody tr td:first-child) {
        max-width: 15%;
        width: 15%;
    }
</style>
